<template>
  <div class="flex flex-col gap-12 text-start md:flex-row md:items-center">
    <div class="flex flex-col">
      <RevLink
        class="body-1-bold cursor-pointer"
        data-qa="title"
        target="_blank"
        @click="handleOpenListingDetail"
      >
        {{ listing.title }}
      </RevLink>

      <span
        class="text-static-default-low body-2 mt-4 max-w-[256px] break-words"
        :data-qa="`listing-${listing.sku}`"
      >
        {{ i18n(translations.listingSKU, { sku: listing.sku }) }}
      </span>

      <div class="mt-12 flex flex-wrap gap-12 md:flex-row md:items-center">
        <Pill
          v-if="shouldDisplayDealOpportunityTag"
          class="bg-static-info-low"
          size="small"
        >
          <template #default="{ iconSize, textSize }">
            <IconDealFilled class="text-static-brand-hi" :size="iconSize" />
            <span :class="`${textSize} text-static-brand-hi`">{{
              i18n(translations.dealOpportunity)
            }}</span>
          </template>
        </Pill>
        <Pill size="small" :tooltip-content="i18n(translations.gradeToolTip)">
          <template #default="{ iconSize, textSize }">
            <IconGrade :size="iconSize" />
            <span :class="textSize">{{ listing.aestheticGrade.label }}</span>
          </template>
        </Pill>
        <Pill
          v-if="listing.dualSim"
          size="small"
          :tooltip-content="i18n(translations.simToolTip)"
        >
          <template #default="{ iconSize, textSize }">
            <IconSim :size="iconSize" />
            <span :class="textSize"> {{ listing.dualSim }}</span>
          </template>
        </Pill>

        <Pill
          v-if="shouldDisplayNewBatteryLabel"
          customClass="bg-static-success-low"
          size="small"
          :tooltip-content="i18n(translations.newBattery)"
        >
          <template #default="{ iconSize }">
            <IconBattery :size="iconSize" />
          </template>
        </Pill>

        <span class="body-2">
          <div v-if="isPublishing" class="flex items-center">
            <RevSpinner size="small" />
            <span class="ml-8">{{ i18n(translations.publishing) }}</span>
          </div>
          <div v-else-if="isArchiving" class="flex items-center">
            <RevSpinner size="small" />
            <span class="ml-8">{{ i18n(translations.archiving) }}</span>
          </div>
          <RevLink
            v-else-if="publicationState === PUBLICATION_STATE.DISABLED"
            @click="handlePublishListing"
          >
            {{ i18n(translations.publishListing) }}
          </RevLink>
          <RevLink v-else @click="handleArchiveListing">
            {{ i18n(translations.archiveListing) }}
          </RevLink>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'
import { ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevLink } from '@ds/components/Link'
import { RevSpinner } from '@ds/components/Spinner'
import { IconBattery } from '@ds/icons/IconBattery'
import { IconDealFilled } from '@ds/icons/IconDealFilled'
import { IconGrade } from '@ds/icons/IconGrade'
import { IconSim } from '@ds/icons/IconSim'

import type { ListingV2 } from '~/api/listings/types'
import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'
import {
  PUBLICATION_STATE,
  SPECIAL_OFFER_TYPES,
} from '~/scopes/listings/Listings.constants'
import {
  QUERY_MODAL_NAME,
  QUERY_MODAL_TAB_NAME,
  TABS_INFO,
} from '~/scopes/listings/pages/index/components/ListingDetailsDrawer/ListingDetailsDrawer.constants'

import { useListings } from '../../../../composables/useListings'
import Pill from '../Pill/Pill.vue'

import translations from './ProductInfo.translations'

const emit = defineEmits(['refresh-listings'])
const props = defineProps<{
  listing: ListingV2
  publicationState: string
}>()

const i18n = useI18n()
const route = useRoute()
const { publishListing, archiveListing } = useListings()
const isPublishing = ref(false)
const isArchiving = ref(false)
const { isFeatureEnabled } = useBusinessFeaturesStore()
const isDealsListingsPageEnabled = isFeatureEnabled(
  'isDealsListingsPageEnabled',
)

const shouldDisplayDealOpportunityTag =
  props.listing?.hasDealOpportunity && isDealsListingsPageEnabled

const shouldDisplayNewBatteryLabel =
  props.listing.specialOfferType?.value === SPECIAL_OFFER_TYPES.NEW_BATTERY

async function handleOpenListingDetail() {
  await navigateTo({
    ...route,
    query: route.query,
    hash: `#${QUERY_MODAL_NAME}=${props.listing.id}#${QUERY_MODAL_TAB_NAME}=${TABS_INFO[0].tabName}`,
    replace: true,
  })
}

async function handlePublishListing() {
  try {
    isPublishing.value = true
    await publishListing(props.listing.id)
    emit('refresh-listings')
  } finally {
    isPublishing.value = false
  }
}

async function handleArchiveListing() {
  try {
    isArchiving.value = true
    await archiveListing(props.listing.id)
    emit('refresh-listings')
  } finally {
    isArchiving.value = false
  }
}
</script>
